import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import i18next from "i18next";

const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const paragraphs = (value) =>
  escapeHtml(value)
    .split(/\n+/)
    .filter(Boolean)
    .map((text) => `<p>${text}</p>`)
    .join("");

const splitContent = (value, maximum = 2400) => {
  const parts = String(value || "").split(/\n+/).filter(Boolean);
  if (!parts.length) return [];

  const chunks = [];
  let current = "";

  parts.forEach((part) => {
    if (current && current.length + part.length > maximum) {
      chunks.push(current);
      current = part;
    } else {
      current += `${current ? "\n\n" : ""}${part}`;
    }
  });

  if (current) chunks.push(current);
  return chunks;
};

const waitForImages = async (container) => {
  const images = [...container.querySelectorAll("img")];

  await Promise.all(
    images.map(
      (image) =>
        new Promise((resolve) => {
          let finished = false;

          const finish = () => {
            if (finished) return;
            finished = true;
            resolve();
          };

          const timeout = window.setTimeout(finish, 8000);

          const complete = () => {
            window.clearTimeout(timeout);
            finish();
          };

          if (image.complete) {
            complete();
            return;
          }

          image.onload = complete;
          image.onerror = complete;
        })
    )
  );
};

export async function generateBrochure(p, options = {}) {
  const {
    includeFinancial = true,
    includeConfidential = false,
    type = "Public presentation",
    consultant = {},
    language = "en",
    logoUrl = "/panatlantik-logo.png",
    images = [],
  } = options;

  const t = (key, fallback) =>
    i18next.t(key, { lng: language, defaultValue: fallback });

  const typeKeys = {
    "Public presentation": "brochurePublicPresentation",
    "Confidential investor presentation": "brochureConfidentialInvestor",
    "Owner report": "brochureOwnerReport",
    "Property summary": "brochurePropertySummary",
    "Investment memorandum": "brochureInvestmentMemorandum",
    "Development proposal": "brochureDevelopmentProposal",
  };

  const translatedType = t(typeKeys[type] || type, type);

  const currencyLocale = {
    en: "en-GB",
    pt: "pt-PT",
    es: "es-ES",
    fr: "fr-FR",
    de: "de-DE",
    it: "it-IT",
    ru: "ru-RU",
    zh: "zh-CN",
  }[language] || "en-GB";

  const money = (value) => {
    if (value == null || value === "") return "—";
    return new Intl.NumberFormat(currencyLocale, {
      style: "currency",
      currency: p.currency || "EUR",
      maximumFractionDigits: 0,
    }).format(Number(value));
  };

  const cleanImages = [...new Set(images.filter(Boolean))].slice(0, 4);

  const fact = (label, value) => {
    if (value == null || value === "") return "";
    return `
      <div class="fact">
        <span>${escapeHtml(label)}</span>
        <strong>${escapeHtml(value)}</strong>
      </div>
    `;
  };

  const section = (label, value) => {
    if (!value) return "";
    return `
      <section class="section">
        <h2>${escapeHtml(label)}</h2>
        ${paragraphs(value)}
      </section>
    `;
  };

  const header = () => `
    <header>
      <div class="brand">
        <img src="${escapeHtml(logoUrl)}" alt="Panatlantik International" />
        <div>
          <strong>PANATLANTIK INTERNATIONAL</strong>
          <small>REAL ESTATE CRM</small>
        </div>
      </div>
      <div class="document-type">${escapeHtml(translatedType)}</div>
    </header>
  `;

  const footer = (pageNumber) => `
    <footer>
      <div>
        ${escapeHtml(t("pdfCommercialEnquiries", "Commercial enquiries"))}
        · ${escapeHtml(consultant.consultant_email || "admin@panatlantik.com")}
        · ${escapeHtml(consultant.consultant_phone || "+351 934 158 151")}
      </div>
      <div>${escapeHtml(t("pdfConfidentialNotice", "Confidential document"))} · ${pageNumber}</div>
    </footer>
  `;

  const pages = [];

  pages.push(`
    <article class="pdf-page">
      ${header()}
      <main>
        <div class="title-block">
          <div class="reference">${escapeHtml(p.reference || "")}</div>
          <h1>${escapeHtml(p.title || t("property", "Property"))}</h1>
          <div class="subtitle">
            ${escapeHtml(p.category || "")}
            ${p.city ? ` · ${escapeHtml(p.city)}` : ""}
            ${p.district ? `, ${escapeHtml(p.district)}` : ""}
          </div>
        </div>

        ${
          cleanImages[0]
            ? `<img class="hero" src="${escapeHtml(cleanImages[0])}" alt="${escapeHtml(p.title)}" />`
            : ""
        }

        ${
          cleanImages.length > 1
            ? `<div class="gallery">
                ${cleanImages
                  .slice(1)
                  .map(
                    (image) =>
                      `<img src="${escapeHtml(image)}" alt="${escapeHtml(p.title)}" />`
                  )
                  .join("")}
               </div>`
            : ""
        }

        <div class="facts">
          ${fact(t("askingPrice2", "Asking price"), Number(p.asking_price) > 0
            ? money(p.asking_price)
            : t("priceUponRequest", "Price upon request"))}
          ${fact(t("landArea", "Land area"), p.land_area ? `${p.land_area} m²` : "")}
          ${fact(t("grossArea", "Gross area"), p.gross_area ? `${p.gross_area} m²` : "")}
          ${fact(t("transactionType", "Transaction"), p.transaction_type)}
        </div>
      </main>
      ${footer(1)}
    </article>
  `);

  const descriptionChunks = splitContent(p.description);

  descriptionChunks.forEach((chunk, index) => {
    pages.push(`
      <article class="pdf-page">
        ${header()}
        <main>
          <section class="section">
            <h2>${escapeHtml(t("description", "Description"))}</h2>
            ${paragraphs(chunk)}
          </section>

          ${
            index === descriptionChunks.length - 1
              ? `<div class="details">
                  ${fact(t("location", "Location"), [p.address, p.postal_code, p.city].filter(Boolean).join(" "))}
                  ${fact(t("category", "Category"), p.category)}
                  ${fact(t("bedrooms", "Bedrooms"), p.bedrooms)}
                  ${fact(t("bathrooms", "Bathrooms"), p.bathrooms)}
                  ${fact(t("floors", "Floors"), p.floors)}
                  ${fact(t("yearBuilt", "Year built"), p.year_built)}
                  ${fact(t("energyCertificate", "Energy certificate"), p.energy_certificate)}
                  ${fact(t("condition", "Condition"), p.condition)}
                </div>`
              : ""
          }
        </main>
        ${footer(pages.length + 1)}
      </article>
    `);
  });

  const investmentSections = [
    includeFinancial
      ? section(
          t("developmentPotential", "Development potential"),
          p.development_potential
        )
      : "",
    includeConfidential
      ? section(
          t("investmentStrengths", "Investment strengths"),
          p.investment_strengths
        )
      : "",
    includeConfidential
      ? section(
          t("investmentRisks", "Investment risks"),
          p.investment_risks
        )
      : "",
    includeConfidential
      ? section(t("exitStrategy", "Exit strategy"), p.exit_strategy)
      : "",
  ].filter(Boolean);

  if (includeFinancial || investmentSections.length) {
    pages.push(`
      <article class="pdf-page">
        ${header()}
        <main>
          ${
            includeFinancial
              ? `<div class="financial">
                  ${fact(t("askingPrice2", "Asking price"), Number(p.asking_price) > 0
            ? money(p.asking_price)
            : t("priceUponRequest", "Price upon request"))}
                  ${fact(t("estimatedRoi", "Estimated ROI"), p.estimated_roi ? `${p.estimated_roi}%` : "")}
                  ${
                    includeConfidential
                      ? fact(t("minimumPrice", "Minimum price"), money(p.min_price))
                      : ""
                  }
                  ${
                    includeConfidential
                      ? fact(t("marketValue", "Market value"), money(p.market_value))
                      : ""
                  }
                </div>`
              : ""
          }
          ${investmentSections.join("")}
        </main>
        ${footer(pages.length + 1)}
      </article>
    `);
  }

  const stage = document.createElement("div");
  stage.setAttribute("aria-hidden", "true");
  stage.style.position = "fixed";
  stage.style.left = "-10000px";
  stage.style.top = "0";
  stage.style.width = "794px";

  stage.innerHTML = `
    <style>
      .pdf-page {
        box-sizing: border-box;
        position: relative;
        width: 794px;
        min-height: 1123px;
        padding: 118px 54px 76px;
        overflow: hidden;
        background: #ffffff;
        color: #0b2545;
        font-family: Arial, "Noto Sans", "Microsoft YaHei",
          "PingFang SC", "Segoe UI", sans-serif;
      }

      header {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 92px;
        padding: 15px 42px;
        box-sizing: border-box;
        background: #0b2545;
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .brand {
        display: flex;
        align-items: center;
        gap: 14px;
      }

      .brand img {
        width: 62px;
        height: 62px;
        object-fit: contain;
      }

      .brand strong {
        display: block;
        font-family: Georgia, serif;
        font-size: 21px;
        letter-spacing: 0.4px;
      }

      .brand small {
        display: block;
        margin-top: 4px;
        color: #c5a059;
        font-size: 10px;
        letter-spacing: 3px;
      }

      .document-type {
        max-width: 230px;
        color: #c5a059;
        font-size: 12px;
        font-weight: 700;
        text-align: right;
        text-transform: uppercase;
      }

      main {
        width: 100%;
      }

      .title-block {
        margin-bottom: 22px;
      }

      .reference {
        color: #c5a059;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 1.2px;
      }

      h1 {
        margin: 7px 0 7px;
        font-family: Georgia, "Times New Roman", serif;
        font-size: 31px;
        line-height: 1.15;
      }

      .subtitle {
        color: #64748b;
        font-size: 14px;
      }

      .hero {
        display: block;
        width: 100%;
        height: 390px;
        border: 1px solid #e2e8f0;
        object-fit: cover;
      }

      .gallery {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
        margin-top: 9px;
      }

      .gallery img {
        width: 100%;
        height: 105px;
        object-fit: cover;
      }

      .facts,
      .details,
      .financial {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1px;
        margin-top: 22px;
        background: #e2e8f0;
        border: 1px solid #e2e8f0;
      }

      .fact {
        min-height: 55px;
        padding: 12px 15px;
        box-sizing: border-box;
        background: #f8fafc;
      }

      .fact span {
        display: block;
        margin-bottom: 5px;
        color: #94a3b8;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.8px;
        text-transform: uppercase;
      }

      .fact strong {
        color: #0b2545;
        font-size: 15px;
      }

      .section {
        margin-bottom: 25px;
      }

      .section h2 {
        margin: 0 0 13px;
        padding-bottom: 9px;
        border-bottom: 2px solid #c5a059;
        font-family: Georgia, "Times New Roman", serif;
        font-size: 23px;
      }

      .section p {
        margin: 0 0 13px;
        color: #334155;
        font-size: 14px;
        line-height: 1.58;
      }

      footer {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 48px;
        padding: 0 42px;
        background: #0b2545;
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 9px;
      }

      footer div:last-child {
        color: #c5a059;
        text-transform: uppercase;
      }
    </style>
    ${pages.join("")}
  `;

  document.body.appendChild(stage);

  try {
    await waitForImages(stage);

    const pageElements = [...stage.querySelectorAll(".pdf-page")];
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
      compress: true,
    });

    for (let index = 0; index < pageElements.length; index += 1) {
      const canvas = await html2canvas(pageElements[index], {
        scale: 1.6,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        imageTimeout: 8000,
        logging: false,
      });

      if (index > 0) pdf.addPage();

      const imageData = canvas.toDataURL("image/jpeg", 0.9);
      pdf.addImage(
        imageData,
        "JPEG",
        0,
        0,
        pdf.internal.pageSize.getWidth(),
        pdf.internal.pageSize.getHeight(),
        undefined,
        "FAST"
      );
    }

    pdf.save(`${p.reference || "property"}-${language}-brochure.pdf`);
  } finally {
    stage.remove();
  }
}
